import type { CoverStyle } from '@pulsify/shared/dto'
import { type ReactNode, useId } from 'react'

const INK = '#17151a'
const NIGHT = '#1d0f2c'
const PLUM = '#3b1650'
const AMBER = '#ffb347'
const PAPER = '#f2ede4'
const BRAND = 'var(--brand)'

const THUMBS: Record<CoverStyle, (id: string) => ReactNode> = {
  grafik: () => (
    <>
      <rect width="120" height="80" fill={NIGHT} />
      <circle cx="82" cy="34" r="22" fill={BRAND} />
      <circle cx="82" cy="34" r="13" fill={AMBER} />
      <path d="M0 62 Q30 46 60 60 T120 54 V80 H0z" fill={PLUM} />
      <path d="M0 70 Q40 58 80 68 T120 66 V80 H0z" fill={BRAND} opacity=".55" />
    </>
  ),
  poster: () => (
    <>
      <rect width="120" height="80" fill={PAPER} />
      <rect x="8" y="8" width="46" height="64" fill={INK} />
      <rect x="60" y="8" width="52" height="30" fill={BRAND} />
      <rect x="60" y="42" width="24" height="30" fill={AMBER} />
      <circle cx="99" cy="57" r="13" fill="none" stroke={INK} strokeWidth="4" />
      <rect x="14" y="54" width="34" height="5" fill={PAPER} />
      <rect x="14" y="62" width="22" height="3" fill={PAPER} />
    </>
  ),
  '3d': (id) => (
    <>
      <defs>
        <radialGradient id={`${id}-ball`} cx=".35" cy=".3">
          <stop offset="0" stopColor="#ffe0b0" />
          <stop offset=".45" stopColor={BRAND} />
          <stop offset="1" stopColor="#5a1220" />
        </radialGradient>
        <linearGradient id={`${id}-sky`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2a1530" />
          <stop offset="1" stopColor="#0f0a14" />
        </linearGradient>
      </defs>
      <rect width="120" height="80" fill={`url(#${id}-sky)`} />
      <ellipse cx="62" cy="66" rx="30" ry="5" fill="#000" opacity=".45" />
      <circle cx="60" cy="38" r="24" fill={`url(#${id}-ball)`} />
      <path d="M40 30 Q60 20 80 30" stroke="#fff" strokeOpacity=".35" strokeWidth="2" fill="none" />
    </>
  ),
  minimal: () => (
    <>
      <rect width="120" height="80" fill="#f6f4f0" />
      <circle cx="60" cy="36" r="14" fill="none" stroke={BRAND} strokeWidth="2" />
      <rect x="38" y="60" width="44" height="3" rx="1.5" fill={INK} />
      <rect x="48" y="67" width="24" height="2" rx="1" fill="#9b96a3" />
    </>
  ),
  typo: () => (
    <>
      <rect width="120" height="80" fill={INK} />
      <rect x="8" y="16" width="70" height="22" rx="2" fill={BRAND} />
      <rect x="8" y="46" width="92" height="22" rx="2" fill="none" stroke="#f3f1f5" />
      <rect x="84" y="16" width="16" height="22" rx="2" fill={BRAND} opacity=".5" />
    </>
  ),
  retro: () => (
    <>
      <rect width="120" height="80" fill="#2b1236" />
      <circle cx="60" cy="52" r="26" fill={AMBER} />
      <g fill="#2b1236">
        <rect x="30" y="50" width="60" height="3" />
        <rect x="30" y="57" width="60" height="4" />
        <rect x="30" y="65" width="60" height="5" />
      </g>
      <path d="M0 80 L60 58 L120 80z" fill={BRAND} opacity=".8" />
      <path d="M0 72 H120M0 76 H120" stroke={BRAND} strokeOpacity=".5" />
    </>
  ),
}

export function StyleThumb({ style }: { style: CoverStyle }) {
  const id = useId()
  return (
    <svg
      viewBox="0 0 120 80"
      aria-hidden="true"
      className="block aspect-[3/2] w-full rounded-[9px]"
    >
      {THUMBS[style](id)}
    </svg>
  )
}
